import { useState, useEffect, useRef } from "react";
import { submitDemoRequest, ApiError } from "./api";
import { FOOTER_PHONE } from "./SiteChrome";
import logo from "./assets/logo.png"
// BOOKDEMO..
/* ─── Palette & Tokens ───────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --teal:       #2bb3a3;
    --teal-deep:  #0F4C5C;
    --teal-aqua:  #2EC4B6;
    --blue-soft:  #6FBEDC;
    --indigo:     #6366f1;
    --off-white:  #f0f9ff;
    --charcoal:   #1e293b;
    --muted:      #64748b;
    --border:     rgba(43,179,163,0.15);
    --radius-lg:  20px;
    --radius-sm:  10px;
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: 'DM Sans', 'Inter', sans-serif;
    background: var(--off-white);
    color: var(--charcoal);
    overflow-x: hidden;
    min-height: 100vh;
  }

  ::selection { background: rgba(43,179,163,0.22); }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #e0f2fe; }
  ::-webkit-scrollbar-thumb { background: var(--teal); border-radius: 3px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; } to { opacity: 1; }
  }
  @keyframes pulseRing {
    0%   { transform: scale(1); opacity: 0.55; }
    100% { transform: scale(2); opacity: 0; }
  }
  @keyframes floatOrb {
    0%,100% { transform: translateY(0px) scale(1); }
    50%      { transform: translateY(-18px) scale(1.03); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes checkPop {
    0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
    70%  { transform: scale(1.15) rotate(4deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* ── Scroll reveal ────────────────────────────────────────────────────── */
  .bd-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1);
    will-change: opacity, transform;
  }
  .bd-reveal.visible { opacity: 1; transform: translateY(0); }
  .bd-reveal-delay-1 { transition-delay: 0.1s; }
  .bd-reveal-delay-2 { transition-delay: 0.2s; }

  /* ── Back button (replaces the navbar) ────────────────────────────────── */
  .bd-back {
    position: fixed;
    top: clamp(14px, 1.8vw, 26px);
    left: clamp(14px, 2vw, 32px);
    z-index: 200;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 18px 10px 14px;
    border-radius: 100px;
    border: 1px solid rgba(43,179,163,0.28);
    background: rgba(255,255,255,0.82);
    backdrop-filter: blur(14px) saturate(150%);
    -webkit-backdrop-filter: blur(14px) saturate(150%);
    box-shadow: 0 4px 18px rgba(15,76,92,0.10);
    font-family: 'Inter', 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; letter-spacing: 0.2px;
    color: var(--teal-deep);
    cursor: pointer;
    transition: background 0.25s, box-shadow 0.25s, transform 0.25s, border-color 0.25s;
    animation: fadeIn 0.4s ease both;
  }
  .bd-back:hover {
    background: #ffffff;
    border-color: rgba(43,179,163,0.5);
    box-shadow: 0 8px 26px rgba(15,76,92,0.16);
    transform: translateY(-1px);
  }
  .bd-back svg { width: 17px; height: 17px; transition: transform 0.25s; }
  .bd-back:hover svg { transform: translateX(-3px); }

  /* ── Page layout ──────────────────────────────────────────────────────── */
.bd-page {
    display: flex;
    min-height: 100vh;
    position: relative;
    overflow: hidden;
  }

  /* ── Left panel ──────────────────────────────────────────────────────── */
  .bd-left {
    width: 24%;
    background: linear-gradient(155deg, var(--teal-deep) 0%, #0a3040 55%, #071e2a 100%);
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 80px 52px;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    min-height: 100vh;
  }

  .bd-left-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
  }

  .bd-brand {
    font-family: 'Inter', sans-serif;
    font-weight: 700; font-size: 22px;
    background: linear-gradient(90deg, var(--teal-aqua), var(--blue-soft));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 52px;
    animation: fadeIn 0.6s ease both;
  }
  .bd-left-headline {
    font-family: 'Inter', sans-serif;
    font-size: clamp(28px, 2.8vw, 42px);
    font-weight: 700; color: white;
    line-height: 1.15; letter-spacing: -0.8px;
    margin-bottom: 18px;
    animation: fadeUp 0.6s ease 0.2s both;
  }
  .bd-left-headline .grad {
    background: linear-gradient(90deg, var(--teal-aqua) 0%, var(--blue-soft) 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .bd-left-sub {
    font-size: 15px; color: rgba(255,255,255,0.42);
    line-height: 1.7; max-width: 320px;
    animation: fadeUp 0.6s ease 0.3s both;
    font-weight: 300;
  }

  .bd-left-divider {
    width: 48px; height: 2px;
    background: linear-gradient(90deg, var(--teal-aqua), var(--blue-soft));
    border-radius: 2px;
    margin: 32px 0;
    animation: fadeUp 0.8s ease 0.5s both;
  }

  .bd-benefits { display: flex; flex-direction: column; gap: 18px; }
  .bd-benefit {
    display: flex; align-items: flex-start; gap: 14px;
    animation: fadeUp 0.5s ease both;
  }
  .bd-benefit-icon-wrap {
    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
    background: rgba(46,196,182,0.12);
    border: 1px solid rgba(46,196,182,0.22);
    display: flex; align-items: center; justify-content: center;
    margin-top: 2px;
  }
  .bd-benefit-icon-wrap svg { width: 17px; height: 17px; color: var(--teal-aqua); stroke: var(--teal-aqua); }
  .bd-benefit-icon-wrap { transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), background 0.3s ease, border-color 0.3s ease; }
  .bd-benefit:hover .bd-benefit-icon-wrap {
    transform: scale(1.1) rotate(-4deg);
    background: rgba(46,196,182,0.2);
    border-color: rgba(46,196,182,0.4);
  }
  .bd-benefit-text { font-size: 14px; color: rgba(255,255,255,0.68); line-height: 1.55; font-weight: 400; }
  .bd-benefit-text strong { color: white; font-weight: 600; display: block; margin-bottom: 2px; }

  .bd-stats {
    display: flex; gap: 32px; margin-top: 40px;
    animation: fadeUp 0.5s ease 0.7s both;
  }
  .bd-stat { text-align: left; }
  .bd-stat-num {
    font-size: 28px; font-weight: 700;
    background: linear-gradient(90deg, var(--teal-aqua), var(--blue-soft));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.2;
  }
  .bd-stat-lbl { font-size: 11px; color: rgba(255,255,255,0.32); text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }

  /* ── Right panel ──────────────────────────────────────────────────────── */
    .bd-right {
        flex: 1;
        display: grid;
        grid-template-columns: 1fr 1.8fr;
        gap: 28px;
        padding: 40px 0 40px 36px;
        position: relative;
        overflow: hidden;
        background: var(--off-white);
        min-width: 0;
        min-height: 100vh;
    }

  /* ── Form card ────────────────────────────────────────────────────────── */
 .bd-form-card {
    background: white;
    border-radius: var(--radius-lg);
    padding: 44px 40px;
    box-shadow:
      0 1px 3px rgba(15,76,92,0.04),
      0 8px 32px rgba(15,76,92,0.08),
      0 24px 64px rgba(15,76,92,0.05);
    animation: fadeUp 0.7s ease 0.15s both;
    position: relative;
    align-self: flex-start;
    min-width: 0;
    transition: box-shadow 0.4s ease;
  }

  .bd-form-card::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    background: linear-gradient(90deg, var(--teal-deep), var(--teal-aqua), var(--indigo));
    z-index: 1;
  }
  .bd-form-card::after {
    content: '';
    position: absolute; inset: 0; border-radius: inherit;
    background: radial-gradient(circle 420px at var(--mx,50%) var(--my,50%), rgba(46,196,182,0.07), transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  .bd-form-card:hover::after { opacity: 1; }

  .bd-form-title {
    font-family: 'Inter', sans-serif;
    font-size: 26px; font-weight: 700;
    color: var(--teal-deep); letter-spacing: -0.5px;
    margin-bottom: 6px;
  }
  .bd-form-sub {
    font-size: 14px; color: var(--muted);
    margin-bottom: 28px; line-height: 1.55;
  }

  .bd-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }

  .bd-field { display: flex; flex-direction: column; gap: 5px; }
  .bd-label {
    font-size: 11px; font-weight: 600;
    color: var(--charcoal); letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .bd-label .req { color: var(--teal-aqua); margin-left: 2px; }

  .bd-input, .bd-select, .bd-textarea {
    width: 100%;
    padding: 10px 13px;
    border-radius: var(--radius-sm);
    border: 1.5px solid #E5EAF0;
    font-family: 'DM Sans', 'Inter', sans-serif;
    font-size: 14px; color: var(--charcoal);
    background: #FAFBFC;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    -webkit-appearance: none;
  }
  .bd-input::placeholder, .bd-textarea::placeholder { color: #B0B8C4; }
  .bd-input:focus, .bd-select:focus, .bd-textarea:focus {
    border-color: var(--teal-aqua);
    box-shadow: 0 0 0 3px rgba(46,196,182,0.1);
    background: white;
  }
  .bd-select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    padding-right: 36px;
    cursor: pointer;
  }
  .bd-textarea { resize: none; min-height: 80px; line-height: 1.6; }

  .bd-section-sep {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(46,196,182,0.2), transparent);
    margin: 14px 0 12px;
  }

  .bd-submit {
    width: 100%;
    padding: 14px;
    border-radius: 100px;
    border: none; cursor: pointer;
    font-family: 'DM Sans', 'Inter', sans-serif;
    font-size: 15px; font-weight: 600;
    background: linear-gradient(90deg, var(--teal), var(--indigo));
    background-size: 200% auto;
    color: white;
    letter-spacing: 0.3px;
    transition: background-position 0.4s, transform 0.2s, box-shadow 0.2s;
    margin-top: 8px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    position: relative; overflow: hidden;
  }
  .bd-submit::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
    transform: translateX(-100%); transition: transform 0.5s;
  }
  .bd-submit:not(:disabled):hover {
    background-position: right center;
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(43,179,163,0.38);
  }
  .bd-submit:not(:disabled):hover::after { transform: translateX(100%); }
  .bd-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .bd-submit .arrow { font-size: 16px; transition: transform 0.2s; }
  .bd-submit:not(:disabled):hover .arrow { transform: translateX(4px); }

  .bd-privacy {
    font-size: 11px; color: #B0B8C4;
    text-align: center; margin-top: 12px; line-height: 1.55;
  }
  .bd-privacy a { color: var(--teal-aqua); text-decoration: none; }

  .bd-spinner {
    width: 17px; height: 17px;
    border: 2px solid rgba(255,255,255,0.35);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  /* ── Success ──────────────────────────────────────────────────────────── */
  .bd-success {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 20px 10px;
    min-height: 360px;
    animation: fadeIn 0.5s ease;
  }
  .bd-success-ring {
    width: 80px; height: 80px; border-radius: 50%;
    background: linear-gradient(135deg, rgba(46,196,182,0.12), rgba(15,76,92,0.06));
    border: 2px solid rgba(46,196,182,0.3);
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 20px;
    animation: checkPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both;
  }
  .bd-success-ring svg { width: 36px; height: 36px; stroke: var(--teal-aqua); }
  .bd-success-title {
    font-family: 'Inter', sans-serif; font-size: 22px; font-weight: 700;
    color: var(--teal-deep); margin-bottom: 8px;
  }
  .bd-success-sub { font-size: 14px; color: var(--muted); line-height: 1.65; max-width: 300px; }
  .bd-success-home {
    margin-top: 28px; padding: 13px 32px;
    border-radius: 100px;
    background: linear-gradient(90deg, var(--teal), var(--indigo));
    color: white; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600;
    transition: all 0.25s; letter-spacing: 0.2px;
  }
  .bd-success-home:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(43,179,163,0.35); }

  /* ── Address / Map Panel ──────────────────────────────────────────────── */
  /*
   * KEY FIX: flex: 1 makes the panel fill ALL remaining horizontal space.
   * align-self: stretch (inherited from bd-right's align-items: stretch)
   * makes it match the full height of the row.
   * The inner flex column stretches the map card to fill that height.
   */
    .bd-address-panel {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 14px;
        }
  .bd-office-toggle {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
  }

  .bd-office-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: var(--radius-sm);
    border: 1.5px solid #E5EAF0;
    background: white;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--muted);
    cursor: pointer;
    transition: all 0.22s;
    box-shadow: 0 1px 4px rgba(15,76,92,0.04);
  }
  .bd-office-btn:hover {
    border-color: rgba(46,196,182,0.4);
    color: var(--charcoal);
    transform: translateY(-1px);
  }
  .bd-office-btn--active {
    border-color: var(--teal-aqua);
    background: white;
    color: var(--charcoal);
    font-weight: 600;
    box-shadow: 0 0 0 3px rgba(46,196,182,0.12), 0 2px 8px rgba(15,76,92,0.06);
  }
  .bd-office-btn--active-indigo {
    border-color: var(--indigo);
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12), 0 2px 8px rgba(15,76,92,0.06);
  }

  .bd-office-btn-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: background 0.22s;
  }

  /* Map card stretches to fill remaining height of the panel */
.bd-map-card {
    flex: 1;
    min-height: 300px;
    border-radius: var(--radius-lg) 0 0 var(--radius-lg);
    overflow: hidden;
    box-shadow:
      0 1px 3px rgba(15,76,92,0.05),
      0 8px 32px rgba(15,76,92,0.10);
    position: relative;
    display: flex;
    flex-direction: column;
    transition: transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.4s ease;
    will-change: transform;
  }
  .bd-map-card:hover {
    box-shadow:
      0 4px 10px rgba(15,76,92,0.08),
      0 16px 44px rgba(15,76,92,0.16);
  }
  .bd-map-card::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--teal-deep), var(--teal-aqua), var(--indigo));
    z-index: 1;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }

  /* iframe fills the entire map card */
  .bd-map-iframe {
    flex: 1;
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    min-height: 300px;
  }

.bd-map-info {
    flex-shrink: 0;
    background: white;
    border-radius: var(--radius-lg);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    box-shadow:
      0 1px 3px rgba(15,76,92,0.04),
      0 4px 16px rgba(15,76,92,0.07);
  }

  .bd-map-info-left { flex: 1; min-width: 0; }

  .bd-map-info-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 9px; border-radius: 100px;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.8px; text-transform: uppercase;
    margin-bottom: 8px;
  }
  .bd-map-info-badge--teal {
    background: rgba(46,196,182,0.1);
    border: 1px solid rgba(46,196,182,0.25);
    color: var(--teal-aqua);
  }
  .bd-map-info-badge--indigo {
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.25);
    color: var(--indigo);
  }

  .bd-map-info-name {
    font-family: 'Inter', sans-serif;
    font-size: 14px; font-weight: 700;
    color: var(--teal-deep); margin-bottom: 4px;
  }

  .bd-map-info-addr {
    font-size: 12px; color: var(--muted); line-height: 1.6;
  }

  .bd-map-open-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 15px;
    border-radius: 100px;
    background: linear-gradient(90deg, var(--teal), var(--indigo));
    color: white;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px; font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
    transition: all 0.25s;
    flex-shrink: 0;
  }
  .bd-map-open-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(43,179,163,0.35);
  }

  /* ── Footer ───────────────────────────────────────────────────────────── */
  .bd-footer {
    display: flex;
    background: #0a0f1e;
    padding: 32px 60px;
    align-items: center; justify-content: space-between;
    border-top: 1px solid rgba(43,179,163,0.1);
    width: 100%;
  }
  .bd-footer-logo {
    font-family: 'Inter', sans-serif; font-weight: 800; font-size: 20px;
    background: linear-gradient(90deg, var(--teal), var(--indigo));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .bd-footer-copy { font-size: 13px; color: rgba(255,255,255,0.3); }
  .bd-footer-socials { display: flex; gap: 12px; }
  .bd-footer-social {
    width: 38px; height: 38px; border-radius: 50%;
    border: 1px solid rgba(43,179,163,0.25);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.25s; color: rgba(255,255,255,0.5);
    background: transparent;
  }
  .bd-footer-social:hover {
    background: linear-gradient(135deg, var(--teal), var(--indigo));
    border-color: transparent; color: white;
    transform: translateY(-3px); box-shadow: 0 6px 20px rgba(43,179,163,0.35);
  }
  .sm-brand {
    display: flex; align-items: center; gap: 12px;
    position: relative;
  }

  /* ── Responsive Breakpoints ────────────────────────────────────────────
     Structural lines match the site-wide ladder (see MobileAppStyles.js):
     <=479 small phone · 480-733 large phone · 734-1068 tablet ·
     1069-1441 laptop · >=1442 large desktop. The 1100px rule below is the one
     exception  it is not a device class, it is the width at which this
     page's own right-hand panel runs out of room for two columns. */

  /* The right panel stops fitting a form beside a map at ~1100px */
  @media (max-width: 1100px) {
    .bd-right {
      grid-template-columns: 1fr;
      align-items: center;
      padding: 40px 28px;
    }
    .bd-form-card { width: 100%; max-width: 600px; margin: 0 auto; }
    .bd-address-panel {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 14px;
      padding-right: 0;
      max-width: 600px;
      width: 100%;
      margin: 0 auto;
    }
    .bd-map-iframe { min-height: 320px; height: 320px; }
    .bd-map-card { flex: none; border-radius: var(--radius-lg); }
  }

  /* Tablet (734–1068px) */
  @media (max-width: 1068px) {
    .bd-page { flex-direction: column; }
    .bd-left {
      width: 100%;
      /* extra top room so the floating back button never sits on the brand */
      padding: 86px 36px 44px;
      min-height: auto;
    }
    .bd-left-headline { font-size: clamp(26px, 4vw, 36px); }
    .bd-left-sub { max-width: 100%; }
    .bd-stats { flex-wrap: wrap; gap: 20px; }

    .bd-right {
      grid-template-columns: 1fr;
      padding: 32px 24px 40px;
    }

    .bd-map-info {
      flex-direction: column;
      align-items: flex-start;
      gap: 14px;
    }
    .bd-map-open-btn { width: 100%; justify-content: center; }

    .bd-footer-grid-inner {
      grid-template-columns: 1fr 1fr !important;
    }
  }

  /* Large phone / phablet (480–733px) */
  @media (max-width: 733px) {
    .bd-back { font-size: 13px; padding: 9px 16px 9px 12px; }

    .bd-left { padding: 78px 24px 36px; }
    .bd-left-headline { font-size: clamp(24px, 6vw, 32px); }
    .bd-brand { font-size: 18px; margin-bottom: 36px; }

    .bd-right { padding: 24px 16px 36px; }
    .bd-form-card { padding: 28px 20px; }
    .bd-grid-2 { grid-template-columns: 1fr; }

    .bd-office-toggle { flex-direction: column; gap: 8px; }
    .bd-office-btn { justify-content: flex-start; }

    .bd-map-card { border-radius: var(--radius-lg); }
    .bd-map-iframe { min-height: 260px; height: 260px; }

    .bd-stats { gap: 16px; flex-wrap: wrap; }
    .bd-stat-num { font-size: 22px; }

    /* Footer mobile */
    .bd-footer-grid-inner {
      grid-template-columns: 1fr !important;
    }
    .bd-footer-bottom-bar {
      flex-direction: column !important;
      text-align: center;
      gap: 8px !important;
    }
  }

  /* Small phone (<=479px) */
  @media (max-width: 479px) {
    .bd-left { padding: 72px 18px 30px; }
    .bd-left-headline { font-size: clamp(22px, 7vw, 28px); }

    .bd-right { padding: 20px 14px 32px; }
    .bd-form-card { padding: 24px 16px; border-radius: 14px; }
    .bd-form-title { font-size: 20px; }

    .bd-map-iframe { min-height: 220px; height: 220px; }

    .bd-map-info { padding: 14px 16px; }
    .bd-map-info-name { font-size: 13px; }
    .bd-map-info-addr { font-size: 11px; }

    .bd-submit { font-size: 14px; padding: 13px; }

    .bd-footer-grid-inner {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
    }
  }

  /* Field error */
  .bd-field-error { font-size: 11px; color: #ef4444; margin-top: 3px; }

  /* Honeypot. Positioned off-screen rather than display:none  a headless
     browser that skips hidden inputs will still fill one it can "see". */
  /* Not merely offscreen: autofill and password managers skip unrendered
     fields, so this is what stops a real visitor tripping the trap. */
  .bd-hp { display: none; }
  .bd-input-err, .bd-select-err {
    border-color: #ef4444 !important;
    box-shadow: 0 0 0 3px rgba(239,68,68,0.1) !important;
  }
`;

/* ─── SVG Icons ──────────────────────────────────────────────────────────── */
const InstagramIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
  </svg>
);
const LinkedInIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M4.98 3.5C4.98 4.88 3.87 6 2.49 6 1.11 6 0 4.88 0 3.5 0 2.12 1.11 1 2.49 1c1.38 0 2.49 1.12 2.49 2.5zM.5 8h4V24h-4V8zm7.5 0h3.8v2.2h.05c.53-1 1.84-2.2 3.79-2.2 4.05 0 4.8 2.67 4.8 6.14V24h-4v-7.6c0-1.81-.03-4.14-2.52-4.14-2.53 0-2.92 1.98-2.92 4.01V24h-4V8z"/>
  </svg>
);
const FacebookIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12a10 10 0 10-11.5 9.9v-7h-2.7v-2.9h2.7V9.8c0-2.7 1.6-4.2 4.1-4.2 1.2 0 2.4.2 2.4.2v2.6h-1.3c-1.3 0-1.7.8-1.7 1.6v2h2.9l-.5 2.9h-2.4v7A10 10 0 0022 12z"/>
  </svg>
);
const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.137.563 4.136 1.544 5.866L0 24l6.293-1.516A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.805 9.805 0 01-5.037-1.386l-.361-.214-3.737.901.941-3.643-.234-.374A9.789 9.789 0 012.182 12C2.182 6.574 6.574 2.182 12 2.182S21.818 6.574 21.818 12 17.426 21.818 12 21.818z"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const ExternalLinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
  </svg>
);

/* ─── Benefits data ──────────────────────────────────────────────────────── */
const BENEFITS = [
  {
    title: "Answers, Not Sales Pitches",
    desc: "Straight replies on features, pricing and data handling.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
      </svg>
    ),
  },
  {
    title: "Matched to Your Institution",
    desc: "We answer in the context of your classroom scale and structure.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
      </svg>
    ),
  },
  {
    title: "Reply Within One Business Day",
    desc: "Your enquiry reaches our AI specialists directly, not a queue.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
  },
];

/* ─── Office data ────────────────────────────────────────────────────────── */
const OFFICES = [
  {
    id: "main",
    label: "Head Office",
    name: "SGRG Group  Kanpur",
    line1: "1111, Y Block, Kidwai Nagar,",
    line2: "Kanpur, Uttar Pradesh 208011",
    mapSrc: "https://maps.google.com/maps?q=1111+Y+Block+Kidwai+Nagar+Kanpur+Uttar+Pradesh+208011&output=embed",
    mapsUrl: "https://www.google.com/maps/search/1111+Y+Block+Kidwai+Nagar+Kanpur+Uttar+Pradesh+208011",
    accent: "teal",
  },
  {
    id: "corporate",
    label: "Corporate Office",
    name: "SGRG Group  Lucknow",
    line1: "903–904, 9th Floor, Shalimar Iridium,",
    line2: "Gomti Nagar, Lucknow, UP 226010",
    mapSrc: "https://maps.google.com/maps?q=Shalimar+Iridium+Vibhuti+Khand+Gomti+Nagar+Lucknow+226010&output=embed",
    mapsUrl: "https://www.google.com/maps/search/Shalimar+Iridium+Vibhuti+Khand+Gomti+Nagar+Lucknow+226010",
    accent: "indigo",
  },
];

/* ─── Address Panel sub-component ───────────────────────────────────────── */
function AddressPanel() {
  const [active, setActive] = useState("main");
  const office = OFFICES.find((o) => o.id === active);
  const mapCardRef = useRef(null);
  const mapOpenBtnRef = useRef(null);

  const prefersReducedMotion = () =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const handleTilt = (e) => {
    if (prefersReducedMotion() || !mapCardRef.current) return;
    const rect = mapCardRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const rotateY = (px - 0.5) * 4;
    const rotateX = (0.5 - py) * 4;
    mapCardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };
  const resetTilt = () => {
    if (mapCardRef.current) mapCardRef.current.style.transform = "";
  };
  const handleMagnetic = (e) => {
    if (prefersReducedMotion() || !mapOpenBtnRef.current) return;
    const rect = mapOpenBtnRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) * 0.2;
    const y = (e.clientY - rect.top - rect.height / 2) * 0.2;
    mapOpenBtnRef.current.style.transform = `translate(${x}px, ${y}px) translateY(-2px)`;
  };
  const resetMagnetic = () => {
    if (mapOpenBtnRef.current) mapOpenBtnRef.current.style.transform = "";
  };

  return (
    <div className="bd-address-panel bd-reveal bd-reveal-delay-1">

      {/* Toggle buttons */}
      <div className="bd-office-toggle">
        {OFFICES.map((o) => (
          <button
            key={o.id}
            className={[
              "bd-office-btn",
              active === o.id ? "bd-office-btn--active" : "",
              active === o.id && o.accent === "indigo" ? "bd-office-btn--active-indigo" : "",
            ].join(" ")}
            onClick={() => setActive(o.id)}
          >
            <span
              className="bd-office-btn-dot"
              style={{
                background: active === o.id
                  ? o.accent === "indigo" ? "var(--indigo)" : "var(--teal-aqua)"
                  : "#B0B8C4",
              }}
            />
            {o.label}
          </button>
        ))}
      </div>

      {/* Embedded map  flex:1 makes it fill all remaining height */}
      <div
        className="bd-map-card"
        ref={mapCardRef}
        onMouseMove={handleTilt}
        onMouseLeave={resetTilt}
      >
        <iframe
          key={office.id}
          className="bd-map-iframe"
          src={office.mapSrc}
          title={office.label}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      {/* Info row */}
      <div className="bd-map-info">
        <div className="bd-map-info-left">
          <div className={`bd-map-info-badge bd-map-info-badge--${office.accent === "indigo" ? "indigo" : "teal"}`}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%", display: "inline-block",
              background: office.accent === "indigo" ? "var(--indigo)" : "var(--teal-aqua)",
            }} />
            {office.label}
          </div>
          <div className="bd-map-info-name">{office.name}</div>
          <div className="bd-map-info-addr">
            {office.line1}<br />{office.line2}
          </div>
        </div>
        <a
          className="bd-map-open-btn"
          ref={mapOpenBtnRef}
          onMouseMove={handleMagnetic}
          onMouseLeave={resetMagnetic}
          href={office.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLinkIcon />
          Open in Maps
        </a>
      </div>

    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function BookDemo({ onNavigate, onBack, backLabel = "Back" }) {
  const goToSection = onNavigate || (onBack ? () => onBack() : undefined);
  // Whoever mounted this page owns the return trip, so back never needs to know
  // which page it came from.
  const goBack = () => (onBack ? onBack() : goToSection?.("home"));
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "",
    association: "", linkedin: "", message: "",
  });
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState("");
  const [errors, setErrors]       = useState({});
  const [lastSubmit, setLastSubmit] = useState(0);
  /* Bot trap. Hidden from sight and from assistive tech, and skipped by the tab
     order, so no real visitor can fill it  an automated submitter that fills
     every input will. The server discards anything that arrives with it set. */
  const [honeypot, setHoneypot] = useState("");

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [k]: val }));

    let msg = "";
    if (k === "phone" && val.trim() && !/^\+?[0-9\s\-().]{7,15}$/.test(val.trim()))
      msg = "Enter a valid phone number.";
    if (k === "email" && val.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()))
      msg = "Enter a valid email address.";
    if (k === "linkedin" && val.trim() && !/^(https?:\/\/)?(www\.)?linkedin\.com\/.+/.test(val.trim()))
      msg = "Enter a valid LinkedIn URL.";

    setErrors((prev) => ({ ...prev, [k]: msg || undefined }));
  };
  const canSubmit = form.name.trim() && form.email.trim() && form.phone.trim() && form.association;
  function validate() {
  const e = {};
  if (!form.name.trim()) e.name = "Full name is required.";
  if (!form.phone.trim()) e.phone = "Phone number is required.";
  else if (!/^\+?[0-9\s\-().]{7,15}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number.";
  if (!form.email.trim()) e.email = "Email address is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email address.";
  if (!form.association) e.association = "Please select an association type.";
  if (form.linkedin.trim() && !/^(https?:\/\/)?(www\.)?linkedin\.com\/.+/.test(form.linkedin.trim()))
    e.linkedin = "Enter a valid LinkedIn URL.";
  return e;
  }
  async function handleSubmit() {
    // A courtesy throttle only. The binding limit is the server's, which this
    // cannot be trusted to enforce  see server/src/routes/demo.js.
    if (Date.now() - lastSubmit < 30000) {
      setError("Please wait 30 seconds before submitting again.");
      return;
    }
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true); setError("");
    try {
      await submitDemoRequest({
        name:        form.name,
        email:       form.email,
        phone:       form.phone,
        association: form.association,
        company:     form.company,
        linkedin:    form.linkedin,
        message:     form.message,
        website:     honeypot,
      });
      setSubmitted(true);
      setLastSubmit(Date.now());
    } catch (err) {
      // The server owns the wording; it never returns internal detail. Field
      // errors are mapped back onto the inputs that caused them.
      if (err instanceof ApiError) {
        if (err.fieldErrors) setErrors(err.fieldErrors);
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  const orbLayerRef = useRef(null);
  const submitBtnRef = useRef(null);
  const formCardRef = useRef(null);

  useEffect(() => {
    const els = document.querySelectorAll(".bd-reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const prefersReducedMotion = () =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const handleOrbParallax = (e) => {
    if (prefersReducedMotion() || !orbLayerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    orbLayerRef.current.style.transform = `translate(${px * 24}px, ${py * 24}px)`;
  };
  const resetOrbParallax = () => {
    if (orbLayerRef.current) orbLayerRef.current.style.transform = "";
  };

  const handleGlow = (e, ref) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * 100;
    const py = ((e.clientY - rect.top) / rect.height) * 100;
    ref.current.style.setProperty("--mx", `${px}%`);
    ref.current.style.setProperty("--my", `${py}%`);
  };

  const handleMagnetic = (e, ref, strength = 0.25) => {
    if (prefersReducedMotion() || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) * strength;
    const y = (e.clientY - rect.top - rect.height / 2) * strength;
    ref.current.style.transform = `translate(${x}px, ${y}px) translateY(-2px)`;
  };
  const resetMagnetic = (ref) => {
    if (ref.current) ref.current.style.transform = "";
  };

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") goBack(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBack, goToSection]);

  return (
    <>
      <style>{CSS}</style>

      {/* ── BACK BUTTON ─────────────────────────────────────────────────────
          No navbar here. The demo is opened from whichever page mounted it
          (LandingPage or MobileAppLanding), so "back" is simply that page's
          own handler  it always returns the visitor where they came from. */}
      <button className="bd-back" onClick={goBack} aria-label={backLabel}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
        {backLabel}
      </button>

      {/* ── MAIN CONTENT ───────────────────────────────────────────────── */}
      <div className="bd-page">

        {/* LEFT PANEL */}
        <div
          className="bd-left"
          onMouseMove={handleOrbParallax}
          onMouseLeave={resetOrbParallax}
        >
          <div ref={orbLayerRef} style={{ position: "absolute", inset: 0, pointerEvents: "none", transition: "transform 0.3s ease-out", willChange: "transform" }}>
            <div className="bd-left-orb" style={{
              width: 340, height: 340, top: -100, right: -110,
              background: "radial-gradient(circle, rgba(46,196,182,0.08) 0%, transparent 70%)",
              animation: "floatOrb 6s ease-in-out infinite",
            }} />
            <div className="bd-left-orb" style={{
              width: 220, height: 220, bottom: 80, left: -70,
              background: "radial-gradient(circle, rgba(111,190,220,0.06) 0%, transparent 70%)",
              animation: "floatOrb 8s ease-in-out 2s infinite",
            }} />
          </div>

          <div className="bd-brand">Sentio Mind</div>

 

          <h1 className="bd-left-headline">
            Talk to Us About<br />
            Behaviour <span className="grad">Intelligence</span>
          </h1>

          <p className="bd-left-sub">
            Questions about how Sentio Mind fits your institution  pricing, rollout, privacy or a walkthrough? Ask us directly.
          </p>

          <div className="bd-left-divider" />

          <div className="bd-benefits">
            {BENEFITS.map((b, i) => (
              <div className="bd-benefit" key={b.title} style={{ animationDelay: `${0.4 + i * 0.12}s` }}>
                <div className="bd-benefit-icon-wrap">{b.icon}</div>
                <div className="bd-benefit-text">
                  <strong>{b.title}</strong>
                  {b.desc}
                </div>
              </div>
            ))}
          </div>


        </div>

        {/* RIGHT PANEL  form + map side by side, both stretch to full height */}
        <div className="bd-right">

          {/* FORM CARD */}
          <div
            className="bd-form-card bd-reveal"
            ref={formCardRef}
            onMouseMove={(e) => handleGlow(e, formCardRef)}
          >
            {submitted ? (
              <div className="bd-success">
                <div className="bd-success-ring"><CheckIcon /></div>
                <div className="bd-success-title">Enquiry Received!</div>
                <p className="bd-success-sub">
                  Thank you, <strong>{form.name}</strong>! Our team will get back to you with an answer within one business day.
                </p>
                {goToSection && (
                  <button className="bd-success-home" onClick={() => goToSection("home")}>Back to Home</button>
                )}
              </div>
            ) : (
              <>
                <div className="bd-form-title">Have an Enquiry?</div>
                <p className="bd-form-sub">Send us your question and our team will get back to you within one business day.</p>

                {/*
                  Honeypot — see the `honeypot` state above.

                  The name, id and absent label are deliberately meaningless.
                  Password managers (1Password, LastPass, Bitwarden, Dashlane)
                  match on those heuristics and will happily fill a field called
                  "website" — they ignore autoComplete="off" — which trips the
                  trap for a real visitor and gets their enquiry silently
                  discarded. Nothing here may look like a real field.

                  The wrapper is display:none rather than positioned offscreen
                  for the same reason: browser autofill and every major password
                  manager skip fields that are not rendered, while an offscreen
                  field is still a fill target. A bot parsing the DOM is
                  unaffected, which is the only reader this is meant to catch.
                */}
                <div className="bd-hp" aria-hidden="true">
                  <input
                    id="bd-ref-src"
                    name="bd_ref_src"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <div className="bd-grid-2">
                <div className="bd-field">
                  <label className="bd-label">Full Name<span className="req">*</span></label>
                  <input className={`bd-input${errors.name ? " bd-input-err" : ""}`} placeholder="Jane Smith" value={form.name} onChange={set("name")} />
                  {errors.name && <span className="bd-field-error">{errors.name}</span>}
                </div>
                  <div className="bd-field">
                    <label className="bd-label">Organisation</label>
                    <input className="bd-input" placeholder="Your school / company" value={form.company} onChange={set("company")} />
                  </div>
                </div>

                <div className="bd-grid-2">
                  <div className="bd-field">
                    <label className="bd-label">Phone Number<span className="req">*</span></label>
                    <input className={`bd-input${errors.phone ? " bd-input-err" : ""}`} type="tel" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={set("phone")} />
                    {errors.phone && <span className="bd-field-error">{errors.phone}</span>}
                  </div>
                  <div className="bd-field">
                    <label className="bd-label">Email Address<span className="req">*</span></label>
                    <input className={`bd-input${errors.email ? " bd-input-err" : ""}`} type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} />
                    {errors.email && <span className="bd-field-error">{errors.email}</span>}
                  </div>
                  </div>

                <div className="bd-grid-2">
                  <div className="bd-field">
                    <label className="bd-label">Association<span className="req">*</span></label>
                    <select className={`bd-select${errors.association ? " bd-select-err" : ""}`} value={form.association} onChange={set("association")}>
                      <option value="">Select type</option>
                      <option value="School">School</option>
                      <option value="College">College</option>
                      <option value="University">University</option>
                      <option value="EdTech Company">EdTech Company</option>
                      <option value="NGO">NGO</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.association && <span className="bd-field-error">{errors.association}</span>}
                  </div>
                    <div className="bd-field">
                      <label className="bd-label">LinkedIn / Portfolio</label>
                      <input className={`bd-input${errors.linkedin ? " bd-input-err" : ""}`} placeholder="linkedin.com/in/you" value={form.linkedin} onChange={set("linkedin")} />
                      {errors.linkedin && <span className="bd-field-error">{errors.linkedin}</span>}
                    </div>
                </div>

                <div className="bd-section-sep" />

                <div className="bd-field" style={{ marginBottom: 18 }}>
                  <label className="bd-label">
                    How can we help?{" "}
                    <span style={{ color: "#B0B8C4", textTransform: "none", fontWeight: 400, fontSize: 11 }}>(Optional)</span>
                  </label>
                  <textarea
                    className="bd-textarea"
                    placeholder="Tell us a little about your institution or what you're looking for..."
                    value={form.message}
                    onChange={set("message")}
                  />
                </div>

                {error && (
                  <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12, textAlign: "center" }}>{error}</p>
                )}

                <button
                  className="bd-submit"
                  ref={submitBtnRef}
                  onClick={handleSubmit}
                  onMouseMove={(e) => handleMagnetic(e, submitBtnRef)}
                  onMouseLeave={() => resetMagnetic(submitBtnRef)}
                  disabled={!canSubmit || loading}
                >
                  {loading ? (
                    <><div className="bd-spinner" /> Sending...</>
                  ) : (
                    <>Send Enquiry <span className="arrow">&#8594;</span></>
                  )}
                </button>

                <p className="bd-privacy">
                  By submitting, you agree to our{" "}
                  <a href="#PrivacyPolicy">Privacy Policy</a>. We'll never share your details.
                </p>
              </>
            )}
          </div>

          {/* ADDRESS + MAP PANEL  flex:1 fills remaining width, stretches to form height */}
          <AddressPanel />

        </div>{/* end .bd-right */}

      </div>{/* end .bd-page */}

    {/* ── FOOTER ── */}
    <footer style={{
      background: "#0a0f1e",
      borderTop: "1px solid rgba(43,179,163,0.1)",
      padding: "60px 60px 32px",
      width: "100%",
      fontFamily: "'Inter', 'DM Sans', sans-serif",
    }}>
      {/* Top grid */}
      <div className="bd-footer-grid-inner bd-reveal" style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr 1fr",
        gap: 48,
        marginBottom: 48,
        paddingBottom: 48,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>

{/* Column 1  Brand */}
        <div>
          <div className="sm-brand" style={{ marginBottom: 10 }}>
            <img
              src={logo}
              alt="Sentio Mind"
              style={{ width: 60, height: 90, objectFit: "contain", borderRadius: 0 }}
            />
            <div style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: 40,
              fontWeight: 600,
              letterSpacing: "-0.2px",
              color: "#ffffff",
            }}>
              Sentio <span style={{ color: "#41b7b6" }}>Mind</span>
            </div>
          </div>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
            A Brand of
          </p>
          <p style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: 24,
            background: "linear-gradient(90deg, #2bb3a3, #6366f1)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginBottom: 14,
          }}>
            SGRG Technologies Private Limited®
          </p>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", lineHeight: 1.7, maxWidth: 280 }}>
            From patterns to prevention  transforming environments through behaviour intelligence.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button className="bd-footer-social" onClick={() => window.open("https://www.instagram.com/sentiomind?igsh=NGd6NGZtdDcxMGpk&utm_source=qr", "_blank", "noopener")}><InstagramIcon size={16} /></button>
            <button className="bd-footer-social" onClick={() => window.open("https://wa.me/7268814449", "_blank", "noopener")}><WhatsAppIcon size={16} /></button>
            <button className="bd-footer-social" onClick={() => window.open("https://www.linkedin.com/company/sentio-mind", "_blank", "noopener")}><LinkedInIcon size={16} /></button>
            <button className="bd-footer-social" onClick={() => window.open("https://www.facebook.com", "_blank", "noopener")}><FacebookIcon size={16} /></button>
          </div>
        </div>

        {/* Column 2  Enquiries (single shared inbox, no personal contacts) */}
        <div>
          <p style={{ fontSize: 15, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#2bb3a3", marginBottom: 20 }}>
            Have an Enquiry?
          </p>
 
          <a
            href="mailto:enquiry@sentiomind.in"
            style={{ fontSize: 20, color: "#2bb3a3", textDecoration: "none", fontWeight: 600, wordBreak: "break-all" }}
          >
            enquiry@sentiomind.in
          </a>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", marginTop: 16, marginBottom: 20 }}>
            Contact
          </p>
          <a href={`tel:+91${FOOTER_PHONE}`} style={{ fontSize: 20, color: "#2bb3a3", textDecoration: "none", fontWeight: 600 }}>
            {FOOTER_PHONE}
          </a>
        </div>

        {/* Column 3  Offices */}
        <div>
          <p style={{ fontSize: 15, fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", color: "#2bb3a3", marginBottom: 20 }}>
            Offices
          </p>
          {[
            {
              label: "Head Office", city: "Kanpur",
              address: "1111, Y Block, Kidwai Nagar,\nKanpur, Uttar Pradesh 208011",
              maps: "https://www.google.com/maps/search/1111+Y+Block+Kidwai+Nagar+Kanpur+208011",
            },
            {
              label: "Corporate Office", city: "Lucknow",
              address: "903–904, 9th Floor, Shalimar Iridium,\nVijaipur Colony, Vibhuti Khand,\nGomti Nagar, Lucknow, UP 226010",
              maps: "https://www.google.com/maps/search/Shalimar+Iridium+Gomti+Nagar+Lucknow+226010",
            },
          ].map(({ label, city, address, maps }) => (
            <div key={label} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 18, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{label}</span>
                <span style={{ fontSize: 16, color: "rgba(255,255,255,0.25)" }}>·</span>
                <span style={{ fontSize: 16, color: "#2bb3a3", fontWeight: 500 }}>{city}</span>
              </div>
              <p style={{ fontSize: 18, color: "rgba(255,255,255,0.35)", lineHeight: 1.75, whiteSpace: "pre-line", marginBottom: 6 }}>
                {address}
              </p>
              <a href={maps} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#6366f1", textDecoration: "none", fontWeight: 500 }}>
                View on Maps →
              </a>
            </div>
          ))}
        </div>

      </div>

      {/* Bottom bar */}
      <div className="bd-footer-bottom-bar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <p style={{ fontSize: 18, color: "rgba(255,255,255,0.22)" }}>
          {new Date().getFullYear()} SGRG Technologies Private Limited. Sentio Mind™. All rights reserved.
        </p>
        <p style={{ fontSize: 18, color: "rgba(255,255,255,0.18)" }}>
          Behaviour Intelligence · DPDP Act 2023 Compliant
        </p>
      </div>
    </footer>

    </>
  );
}
